import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import * as sectionsApi from '@/api/sections';
import * as translationsApi from '@/api/translations';
import { fetchConfigsAdmin, deleteConfig, type FieldConfigResponse } from '@/api/field_config';
import type { Section, SectionCreate } from '@/api/types/core/sections';
import type { Translation } from '@/api/types/core/translations';
import { SectionUtils } from '@/utils/sectionUtils';

// Interface étendue pour les propriétés d'affichage
export interface SectionExt extends Section {
  parent_name?: string;
  editing?: Record<string, boolean>;
  indentationLevel?: number;
  translations?: Translation[];
  fieldConfigs?: FieldConfigResponse[];
}

// Utiliser les types existants de l'API
export type FieldConfig = FieldConfigResponse;

export interface ProblemSection {
  section: SectionExt;
  problemType: 'orphaned' | 'no-type' | 'broken-condition';
  description: string;
  details?: FieldConfigResponse[];
}

export const useSectionsStore = defineStore('sections', () => {
  // État principal
  const sections = ref<SectionExt[]>([]);
  const translations = ref<Translation[]>([]);
  const configs = ref<FieldConfigResponse[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Cache et optimisations
  const lastFetchTime = ref<number>(0);
  const cacheTimeout = 30000; // 30 secondes
  const pendingRequests = ref(new Set<string>());

  // État UI
  const collapsedSections = ref(new Set<string>());
  const editingName = ref<string | null>(null);
  const editingNameValue = ref('');

  // État drag & drop
  const draggedSection = ref<SectionExt | null>(null);
  const dragOverSection = ref<SectionExt | null>(null);
  const dropPosition = ref<'before' | 'after' | 'child'>('after');

  // État des modaux
  const showDeleteModal = ref(false);
  const showTranslationModal = ref(false);
  const showConditionModal = ref(false);
  const showAddRootModal = ref(false);
  const showOrphanModal = ref(false);
  const showGlobalRepairModal = ref(false);

  // Computed properties pour les sections à problèmes
  const orphanedSections = computed(() => {
    return detectOrphanedSections(sections.value);
  });

  const sectionsWithoutType = computed(() => {
    return detectSectionsWithoutType(sections.value);
  });

  const sectionsWithBrokenConditions = computed(() => {
    return detectSectionsWithBrokenConditions(sections.value, configs.value);
  });

  const problemSections = computed((): ProblemSection[] => {
    const problems: ProblemSection[] = [];
    
    // Ajouter les sections orphelines
    orphanedSections.value.forEach(section => {
      problems.push({
        section,
        problemType: 'orphaned',
        description: `Parent manquant: ${SectionUtils.getParentName(section.name)}`
      });
    });

    // Ajouter les sections sans type
    sectionsWithoutType.value.forEach(section => {
      problems.push({
        section,
        problemType: 'no-type',
        description: 'Section finale sans type défini'
      });
    });

    // Ajouter les sections avec conditions cassées
    sectionsWithBrokenConditions.value.forEach(({ section, brokenConfigs }) => {
      problems.push({
        section,
        problemType: 'broken-condition',
        description: `${brokenConfigs.length} condition(s) cassée(s)`,
        details: brokenConfigs
      });
    });

    return problems;
  });

  const problemCount = computed(() => problemSections.value.length);

  // Computed pour les sections organisées hiérarchiquement
  const organizedSections = computed(() => {
    return organizeByHierarchyAndOrder(sections.value);
  });

  // Actions principales avec cache optimisé
  const fetchSectionsData = async (forceRefresh = false): Promise<void> => {
    const requestKey = 'fetchSectionsData';
    
    // Éviter les requêtes multiples simultanées
    if (pendingRequests.value.has(requestKey)) {
      return;
    }

    // Vérifier le cache si pas de force refresh
    const now = Date.now();
    if (!forceRefresh && lastFetchTime.value > 0 && (now - lastFetchTime.value) < cacheTimeout) {
      return;
    }

    pendingRequests.value.add(requestKey);
    loading.value = true;
    error.value = null;
    
    try {
      const rawSections = await sectionsApi.fetchAllSections();
      
      // Traitement des sections avec noms complets
      const sectionsWithDisplay: SectionExt[] = rawSections.map((section: Section) => ({
        id: section.id,
        name: section.name,
        type: section.type,
        display: section.display,
        mandatory_entry: section.mandatory_entry,
        ordre: section.ordre,
        importance: section.importance,
        parent_id: undefined,
        subsections: [],
        parent_name: SectionUtils.getParentName(section.name) || undefined,
        editing: {},
        indentationLevel: SectionUtils.getIndentationLevel(section.name),
        translations: [],
        fieldConfigs: []
      }));

      // Organiser par groupes hiérarchiques et trier par ordre
      const organized = organizeByHierarchyAndOrder(sectionsWithDisplay);
      sections.value = organized;
      
      // Récupérer et associer les traductions et les configurations
      await Promise.all([
        fetchTranslations(),
        fetchConfigs()
      ]);

      lastFetchTime.value = now;
      
    } catch (err) {
      console.error("❌ Erreur lors de la récupération des sections :", err);
      error.value = "Erreur lors de la récupération des sections";
    } finally {
      loading.value = false;
      pendingRequests.value.delete(requestKey);
    }
  };

  const fetchTranslations = async (): Promise<void> => {
    try {
      const response = await translationsApi.fetchTranslations();
      translations.value = Array.isArray(response) ? response : [];
      
      // Associer les traductions et configurations aux sections
      sections.value = sections.value.map(section => ({
        ...section,
        translations: translations.value.filter((t: Translation) => t.reference_id === section.id),
        fieldConfigs: configs.value.filter((c: FieldConfigResponse) => c.reference_id === section.id)
      }));
    } catch (error) {
      console.error("Erreur lors de la récupération des traductions :", error);
      translations.value = [];
    }
  };

  const fetchConfigs = async (): Promise<void> => {
    try {
      const rawConfigs = await fetchConfigsAdmin();
      configs.value = rawConfigs.map((config: unknown) => {
        const configData = config as Record<string, unknown>;
        return {
          id: configData.id as number,
          reference_id: configData.reference_id as number,
          condition_id: configData.condition_id as number,
          condition_value: configData.condition_value as string,
          created_at: configData.created_at as string,
          updated_at: configData.updated_at as string
        };
      });
      
      // Associer les configurations aux sections
      sections.value = sections.value.map(section => ({
        ...section,
        fieldConfigs: configs.value.filter((c: FieldConfigResponse) => c.reference_id === section.id)
      }));
    } catch (err) {
      console.error("❌ Erreur lors de la récupération des configurations :", err);
      error.value = "Erreur lors de la récupération des configurations";
    }
  };

  // Actions CRUD optimisées
  const createSection = async (sectionData: SectionCreate): Promise<{ success: boolean; error?: unknown }> => {
    try {
      const newSection = await sectionsApi.createSections([sectionData]);
      
      // Mise à jour locale optimisée au lieu de recharger tout
      if (newSection && newSection.length > 0) {
        const sectionWithDisplay: SectionExt = {
          ...newSection[0],
          parent_name: SectionUtils.getParentName(newSection[0].name) || undefined,
          editing: {},
          indentationLevel: SectionUtils.getIndentationLevel(newSection[0].name),
          translations: [],
          fieldConfigs: []
        };
        
        sections.value.push(sectionWithDisplay);
        sections.value = organizeByHierarchyAndOrder(sections.value);
      }
      
      return { success: true };
    } catch (error) {
      console.error('❌ Erreur création section:', error);
      // ❌ SUPPRIMÉ : Rechargement automatique en cas d'erreur pour éviter les appels multiples
      // await fetchSectionsData(true);
      return { success: false, error };
    }
  };

  const updateSection = async (sectionId: number, sectionData: Partial<SectionCreate>): Promise<{ success: boolean; error?: unknown }> => {
    try {
      const updatedSection = await sectionsApi.updateSection(sectionId, sectionData);
      
      // Mise à jour locale optimisée
      const index = sections.value.findIndex(s => s.id === sectionId);
      if (index !== -1 && updatedSection) {
        sections.value[index] = {
          ...sections.value[index],
          ...updatedSection,
          parent_name: SectionUtils.getParentName(updatedSection.name) || undefined,
          indentationLevel: SectionUtils.getIndentationLevel(updatedSection.name),
        };
        
        // Réorganiser si le nom a changé
        if (sectionData.name) {
          sections.value = organizeByHierarchyAndOrder(sections.value);
        }
      }
      
      return { success: true };
    } catch (error) {
      console.error('❌ Erreur mise à jour section:', error);
      // ❌ SUPPRIMÉ : Rechargement automatique en cas d'erreur pour éviter les appels multiples
      // await fetchSectionsData(true);
      return { success: false, error };
    }
  };

  const deleteSection = async (sectionId: number): Promise<{ success: boolean; error?: unknown }> => {
    try {
      // Récupérer toutes les sous-sections à supprimer
      const sectionsToDelete = getAllDescendants(sectionId);
      sectionsToDelete.push(sectionId);
      
      // Supprimer toutes les sections en commençant par les sous-sections
      for (const id of sectionsToDelete.reverse()) {
        await sectionsApi.deleteSection(id);
      }
      
      // Mise à jour locale optimisée
      sections.value = sections.value.filter(s => !sectionsToDelete.includes(s.id));
      
      return { success: true };
    } catch (error) {
      console.error('❌ Erreur suppression section:', error);
      // ❌ SUPPRIMÉ : Rechargement automatique en cas d'erreur pour éviter les appels multiples
      // await fetchSectionsData(true);
      return { success: false, error };
    }
  };

  // Actions de réparation
  const repairSection = async (problem: ProblemSection, action: string, newParent?: string): Promise<{ success: boolean; message?: string }> => {
    try {
      switch (action) {
        case 'create-parents':
          if (problem.problemType === 'orphaned') {
            await createMissingParents(problem.section);
            await fetchSectionsData(true);
            return { success: true, message: 'Parents créés avec succès' };
          }
          break;
          
        case 'set-type': {
          if (problem.problemType === 'no-type') {
            const result = await updateSection(problem.section.id, { 
              name: problem.section.name,
              type: 'text',
              display: problem.section.display,
              ordre: problem.section.ordre,
              importance: problem.section.importance,
              mandatory_entry: problem.section.mandatory_entry
            });
            return result.success 
              ? { success: true, message: 'Type défini avec succès' }
              : { success: false, message: 'Erreur lors de la définition du type' };
          }
          break;
        }
          
        case 'fix-conditions':
          if (problem.problemType === 'broken-condition' && problem.details) {
            // Supprimer toutes les conditions cassées
            for (const brokenConfig of problem.details) {
              await deleteConfig(brokenConfig.id);
            }
            await fetchConfigs();
            return { success: true, message: 'Conditions cassées supprimées avec succès' };
          }
          break;
          
        case 'move': {
          if (problem.problemType === 'orphaned' && newParent) {
            const currentName = problem.section.name;
            const lastSegment = SectionUtils.getLastSegment(currentName);
            const newName = newParent === 'ROOT' ? lastSegment : `${newParent}.${lastSegment}`;
            
            const result = await updateSection(problem.section.id, { 
              name: newName,
              type: problem.section.type,
              display: problem.section.display,
              ordre: problem.section.ordre,
              importance: problem.section.importance,
              mandatory_entry: problem.section.mandatory_entry
            });
            return result.success 
              ? { success: true, message: 'Section déplacée avec succès' }
              : { success: false, message: 'Erreur lors du déplacement' };
          }
          break;
        }
          
        case 'delete': {
          const result = await deleteSection(problem.section.id);
          return result.success 
            ? { success: true, message: 'Section supprimée avec succès' }
            : { success: false, message: 'Erreur lors de la suppression' };
        }
      }
      
      return { success: false, message: 'Action non supportée ou conditions non remplies' };
    } catch (error) {
      console.error('Erreur lors de la réparation de la section:', error);
      return { success: false, message: 'Erreur lors de la réparation' };
    }
  };

  // Actions UI
  const toggleCollapse = (sectionName: string): void => {
    if (collapsedSections.value.has(sectionName)) {
      collapsedSections.value.delete(sectionName);
    } else {
      collapsedSections.value.add(sectionName);
    }
  };

  const collapseAll = (): void => {
    sections.value.forEach(section => {
      if (hasChildren(section)) {
        collapsedSections.value.add(section.name);
      }
    });
  };

  const expandAll = (): void => {
    collapsedSections.value.clear();
  };

  // Fonction de nettoyage des noms corrompus
  const cleanCorruptedNames = (sections: SectionExt[]): SectionExt[] => {
    return sections.map(section => {
      const originalName = section.name;
      
      // Détecter et corriger les doublons comme "Protocol.Protocol"
      const segments = originalName.split('.');
      const cleanedSegments: string[] = [];
      
      for (let i = 0; i < segments.length; i++) {
        const segment = segments[i];
        // Éviter les doublons consécutifs
        if (i === 0 || segment !== segments[i - 1]) {
          cleanedSegments.push(segment);
        }
      }
      
      const cleanedName = cleanedSegments.join('.');
      
      // Si le nom a été modifié, corriger dans la base de données
      if (originalName !== cleanedName) {
        console.warn(`🔧 Nom corrompu détecté: "${originalName}" → "${cleanedName}"`);
        // Correction asynchrone dans la base de données
        correctCorruptedNameInDatabase(section.id, cleanedName);
      }
      
      return {
        ...section,
        name: cleanedName,
        parent_name: SectionUtils.getParentName(cleanedName) || undefined,
        indentationLevel: SectionUtils.getIndentationLevel(cleanedName)
      };
    });
  };

  // Fonction pour corriger les noms corrompus dans la base de données
  const correctCorruptedNameInDatabase = async (sectionId: number, correctName: string): Promise<void> => {
    try {
      // Récupérer la section complète pour avoir tous les champs
      const section = sections.value.find(s => s.id === sectionId);
      if (!section) {
        console.error(`❌ Section ${sectionId} non trouvée pour correction`);
        return;
      }
      
      // Envoyer la section complète avec le nom corrigé
      await sectionsApi.updateSection(sectionId, {
        name: correctName,
        type: section.type,
        display: section.display,
        ordre: section.ordre,
        importance: section.importance,
        mandatory_entry: section.mandatory_entry
      });
      console.log(`✅ Nom corrigé en base: "${correctName}"`);
    } catch (error) {
      console.error(`❌ Erreur correction nom en base:`, error);
    }
  };

  const organizeByHierarchyAndOrder = (sections: SectionExt[]): SectionExt[] => {
    // ✅ ÉTAPE 1: Nettoyer les noms corrompus avant l'organisation
    const cleanedSections = cleanCorruptedNames(sections);
    
    const result: SectionExt[] = [];
    const processed = new Set<string>();
    
    const addSectionAndChildren = (parentPath: string): void => {
      const children = cleanedSections.filter(s => {
        const parentPrefix = parentPath ? parentPath + '.' : '';
        const isDirectChild = parentPath === '' 
          ? !s.name.includes('.')
          : s.name.startsWith(parentPrefix) && 
            !s.name.substring(parentPrefix.length).includes('.');
        
        return isDirectChild && !processed.has(s.name);
      });
      
      // ✅ AMÉLIORATION: Tri plus robuste avec gestion des ordres manquants
      children.sort((a, b) => {
        const orderA = a.ordre ?? 999999; // Mettre les sections sans ordre à la fin
        const orderB = b.ordre ?? 999999;
        
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        
        // Si même ordre (ou tous les deux manquants), trier par nom
        return a.name.localeCompare(b.name);
      });
      
      children.forEach(child => {
        if (!processed.has(child.name)) {
          result.push(child);
          processed.add(child.name);
          addSectionAndChildren(child.name);
        }
      });
    };
    
    addSectionAndChildren('');
    
    // ✅ ÉTAPE 3: Validation finale et logging des sections orphelines
    const orphanedSections = cleanedSections.filter(s => !result.some(r => r.name === s.name));
    if (orphanedSections.length > 0) {
      console.warn(`⚠️ ${orphanedSections.length} sections orphelines détectées:`, orphanedSections.map(s => s.name));
      // Ajouter les sections orphelines à la fin pour éviter qu'elles soient perdues
      result.push(...orphanedSections);
    }
    
    return result;
  };

  const detectOrphanedSections = (sections: SectionExt[]): SectionExt[] => {
    const orphaned: SectionExt[] = [];
    
    for (const section of sections) {
      const parentName = SectionUtils.getParentName(section.name);
      
      if (parentName) {
        const parentExists = sections.some(s => s.name === parentName);
        if (!parentExists) {
          orphaned.push(section);
        }
      }
    }
    
    return orphaned;
  };

  const detectSectionsWithoutType = (sections: SectionExt[]): SectionExt[] => {
    return sections.filter(section => {
      const isFinal = !sections.some(s => s.name.startsWith(section.name + '.'));
      return isFinal && (!section.type || section.type.trim() === '');
    });
  };

  const detectSectionsWithBrokenConditions = (sections: SectionExt[], configs: FieldConfigResponse[]): { section: SectionExt; brokenConfigs: FieldConfigResponse[] }[] => {
    const result: { section: SectionExt; brokenConfigs: FieldConfigResponse[] }[] = [];
    
    sections.forEach(section => {
      const sectionConfigs = configs.filter(c => c.reference_id === section.id);
      const brokenConfigs = sectionConfigs.filter(config => {
        // Vérifier si la section conditionnelle existe
        const conditionSection = sections.find(s => s.id === config.condition_id);
        return !conditionSection;
      });
      
      if (brokenConfigs.length > 0) {
        result.push({ section, brokenConfigs });
      }
    });
    
    return result;
  };

  const hasChildren = (section: SectionExt): boolean => {
    return sections.value.some(s => s.name.startsWith(section.name + '.'));
  };

  const isCollapsed = (sectionName: string): boolean => {
    return collapsedSections.value.has(sectionName);
  };

  const shouldShowSection = (section: SectionExt): boolean => {
    const parentName = SectionUtils.getParentName(section.name);
    if (!parentName) return true;
    
    const pathParts = section.name.split('.');
    for (let i = 1; i < pathParts.length; i++) {
      const ancestorPath = pathParts.slice(0, i).join('.');
      if (isCollapsed(ancestorPath)) {
        return false;
      }
    }
    
    return true;
  };

  const getAllDescendants = (sectionId: number | undefined): number[] => {
    if (sectionId === undefined) return [];
    
    const parentSection = sections.value.find(s => s.id === sectionId);
    if (!parentSection) return [];
    
    const parentName = parentSection.name;
    const descendants: number[] = [];
    
    for (const section of sections.value) {
      if (section.name.startsWith(parentName + '.')) {
        descendants.push(section.id);
      }
    }
    
    return descendants;
  };

  const createMissingParents = async (orphanSection: SectionExt): Promise<void> => {
    const parentName = SectionUtils.getParentName(orphanSection.name);
    if (!parentName) return;
    
    const parentsToCreate: string[] = [];
    let currentParent: string | null = parentName;
    
    while (currentParent) {
      const parentExists = sections.value.some(s => s.name === currentParent);
      
      if (!parentExists) {
        parentsToCreate.unshift(currentParent);
      }
      
      currentParent = SectionUtils.getParentName(currentParent);
    }
    
    for (const parentToCreate of parentsToCreate) {
      const parentOfParent = SectionUtils.getParentName(parentToCreate);
      const ordre = parentOfParent ? getNextOrder(parentOfParent) : getNextRootOrder();
      
      const sectionToCreate = {
        name: parentToCreate,
        type: '',
        display: true,
        ordre: ordre,
        importance: 0,
        mandatory_entry: false
      };
      
      await sectionsApi.createSections([sectionToCreate]);
    }
  };

  const renameWithCascade = async (oldFullName: string, newFullName: string): Promise<{ success: boolean; error?: unknown }> => {
    try {
      const sectionsToRename = sections.value.filter(s => 
        s.name === oldFullName || s.name.startsWith(oldFullName + '.')
      );
      
      console.log(`🔄 Renommage en cascade: ${sectionsToRename.length} sections à renommer`);
      
      for (const sectionToRename of sectionsToRename) {
        const oldName = sectionToRename.name;
        let newName: string;
        
        if (oldName === oldFullName) {
          newName = newFullName;
        } else {
          const suffix = oldName.substring(oldFullName.length);
          newName = newFullName + suffix;
        }
        
        // ✅ MISE À JOUR API d'abord - SEULEMENT LE NOM, pas l'ordre
        const updateData = {
          name: newName,
          type: sectionToRename.type,
          display: sectionToRename.display,
          ordre: sectionToRename.ordre, // Garder l'ordre existant
          importance: sectionToRename.importance,
          mandatory_entry: sectionToRename.mandatory_entry
        };
        
        // Appel API seulement, sans mise à jour locale
        const updatedSection = await sectionsApi.updateSection(sectionToRename.id, updateData);
        
        if (!updatedSection) {
          console.error(`❌ Échec du renommage de ${oldName} -> ${newName}`);
          return { success: false, error: `Échec du renommage de ${oldName}` };
        }
        
        // ✅ MISE À JOUR LOCALE avec le nouveau nom
        const index = sections.value.findIndex(s => s.id === sectionToRename.id);
        if (index !== -1) {
          sections.value[index] = {
            ...sections.value[index],
            name: newName,
            parent_name: SectionUtils.getParentName(newName) || undefined,
            indentationLevel: SectionUtils.getIndentationLevel(newName),
          };
        }
        
        console.log(`✅ Renommé: ${oldName} -> ${newName}`);
      }
      
      // ✅ RÉORGANISER après tous les renommages
      sections.value = organizeByHierarchyAndOrder(sections.value);
      
      console.log(`✅ Renommage en cascade terminé avec succès`);
      return { success: true };
    } catch (error) {
      console.error('❌ Erreur lors du renommage en cascade:', error);
      return { success: false, error };
    }
  };

  const getNextOrder = (parentFullName: string): number => {
    const children = sections.value.filter(s => {
      const parentPrefix = parentFullName + '.';
      return s.name.startsWith(parentPrefix) && 
             !s.name.substring(parentPrefix.length).includes('.');
    });
    
    if (children.length === 0) return 1;
    
    const ordres = children.map(s => s.ordre ?? 0);
    return Math.max(...ordres) + 1;
  };

  const getNextRootOrder = (): number => {
    const rootSections = sections.value.filter(s => !s.name.includes('.'));
    
    if (rootSections.length === 0) return 1;
    
    const maxOrder = Math.max(...rootSections.map(s => s.ordre ?? 0));
    return maxOrder + 1;
  };

  // Actions drag & drop
  const performDrop = async (draggedSection: SectionExt, targetSection: SectionExt, position: 'before' | 'after' | 'child'): Promise<{ success: boolean; error?: unknown }> => {
    console.log('🎯 Début performDrop:', {
      draggedSection: draggedSection.name,
      targetSection: targetSection.name,
      position
    });
    
    let operationSuccess = false;
    let operationError: unknown = null;
    
    try {
      if (position === 'child') {
        // ✅ VALIDATION: Vérifier si la section est déjà un enfant direct du parent cible
        const currentParentName = SectionUtils.getParentName(draggedSection.name);
        if (currentParentName === targetSection.name) {
          // La section est déjà un enfant direct du parent cible, pas besoin de la déplacer
          console.log('ℹ️ Section déjà enfant direct du parent cible, aucun déplacement nécessaire');
          operationSuccess = true;
        } else {
          // Déplacer comme enfant de la section cible
          const simpleName = SectionUtils.getLastSegment(draggedSection.name);
          const newFullName = `${targetSection.name}.${simpleName}`;
          
          // ✅ VALIDATION: Vérifier que le nouveau nom n'existe pas déjà
          if (sections.value.some(s => s.name === newFullName)) {
            throw new Error(`Une section avec le nom "${newFullName}" existe déjà.`);
          }
          
          // ✅ VALIDATION: Éviter les déplacements circulaires (enfant vers son propre descendant)
          if (SectionUtils.isDescendantOf(targetSection.name, draggedSection.name)) {
            throw new Error(`Impossible de déplacer "${draggedSection.name}" vers son propre descendant "${targetSection.name}".`);
          }
          
          // Si la section cible avait un type final, le supprimer
          if (targetSection.type && ['text', 'textarea', 'bool', 'number', 'date'].includes(targetSection.type)) {
            const updateResult = await updateSection(targetSection.id, {
              name: targetSection.name,
              type: '',
              display: targetSection.display,
              ordre: targetSection.ordre,
              importance: targetSection.importance,
              mandatory_entry: targetSection.mandatory_entry
            });
            if (!updateResult.success) {
              throw new Error('Erreur lors de la suppression du type de la section cible');
            }
          }
          
          // Calculer le nouvel ordre pour la section enfant
          const newOrder = getNextOrder(targetSection.name);
          
          // Renommer seulement, l'ordre sera mis à jour séparément
          const renameResult = await renameWithCascade(draggedSection.name, newFullName);
          if (!renameResult.success) {
            throw renameResult.error;
          }
          
          // Maintenant mettre à jour l'ordre de la section renommée
          const renamedSection = sections.value.find(s => s.name === newFullName);
          if (renamedSection) {
            const updateResult = await updateSection(renamedSection.id, {
              name: newFullName,
              type: renamedSection.type,
              display: renamedSection.display,
              ordre: newOrder,
              importance: renamedSection.importance,
              mandatory_entry: renamedSection.mandatory_entry
            });
            if (!updateResult.success) {
              throw updateResult.error;
            }
          }
          
          operationSuccess = true;
        }
        
      } else {
        // Déplacer avant ou après (même niveau hiérarchique)
        const currentParentName = SectionUtils.getParentName(draggedSection.name);
        
        // ✅ CORRECTION CRITIQUE : Interpréter l'intention de l'utilisateur
        let targetParentName: string | null;
        
        // Règle 1: Si on glisse une sous-section sur sa section parent avec position before/after,
        // cela signifie qu'on veut la réordonner à l'intérieur du parent, pas la sortir
        if (currentParentName && targetSection.name === currentParentName) {
          // Exemple: HOW.Technic glissé sur HOW → garder dans HOW, changer l'ordre
          targetParentName = currentParentName;
          console.log(`🔧 Réordonnancement détecté: garder ${draggedSection.name} dans ${currentParentName}`);
        } 
        // Règle 2: Si même niveau, garder le même parent
        else if (currentParentName === SectionUtils.getParentName(targetSection.name)) {
          targetParentName = currentParentName;
        } 
        // Règle 3: Sinon, déplacer vers le niveau de la cible
        else {
          targetParentName = SectionUtils.getParentName(targetSection.name);
          console.log(`🔄 Changement de niveau: ${currentParentName} → ${targetParentName}`);
        }
        
        // Calculer le nouvel ordre en fonction de la position
        let newOrder: number;
        const siblings = sections.value.filter(s => {
          const sParentName = SectionUtils.getParentName(s.name);
          return sParentName === targetParentName && s.id !== draggedSection.id;
        }).sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0));
        
        const targetIndex = siblings.findIndex(s => s.id === targetSection.id);
        
        if (position === 'before') {
          if (targetIndex === 0) {
            // Insérer au début
            newOrder = Math.max(1, (targetSection.ordre ?? 1) - 1);
          } else {
            // Insérer entre deux sections
            const prevSection = siblings[targetIndex - 1];
            newOrder = Math.floor(((prevSection.ordre ?? 0) + (targetSection.ordre ?? 0)) / 2);
            if (newOrder === (prevSection.ordre ?? 0)) {
              newOrder = (prevSection.ordre ?? 0) + 1;
            }
          }
        } else { // position === 'after'
          if (targetIndex === siblings.length - 1) {
            // Insérer à la fin
            newOrder = (targetSection.ordre ?? 0) + 1;
          } else {
            // Insérer entre deux sections
            const nextSection = siblings[targetIndex + 1];
            newOrder = Math.floor(((targetSection.ordre ?? 0) + (nextSection.ordre ?? 0)) / 2);
            if (newOrder === (targetSection.ordre ?? 0)) {
              newOrder = (targetSection.ordre ?? 0) + 1;
            }
          }
        }
        
        // ✅ Vérifier si changement de parent nécessaire
        if (targetParentName !== currentParentName) {
          // Changer de parent ET d'ordre
          const simpleName = SectionUtils.getLastSegment(draggedSection.name);
          const newFullName = targetParentName ? `${targetParentName}.${simpleName}` : simpleName;
          
          // ✅ VALIDATION: Vérifier que le nouveau nom n'existe pas déjà
          if (newFullName !== draggedSection.name && sections.value.some(s => s.name === newFullName)) {
            throw new Error(`Une section avec le nom "${newFullName}" existe déjà.`);
          }
          
          // ✅ VALIDATION: Éviter les déplacements circulaires
          if (targetParentName && SectionUtils.isDescendantOf(targetParentName, draggedSection.name)) {
            throw new Error(`Impossible de déplacer "${draggedSection.name}" vers son propre descendant.`);
          }
          
          // Renommer si nécessaire
          if (newFullName !== draggedSection.name) {
            const renameResult = await renameWithCascade(draggedSection.name, newFullName);
            if (!renameResult.success) {
              throw renameResult.error;
            }
            
            // Après renommage, mettre à jour l'ordre de la section renommée
            const renamedSection = sections.value.find(s => s.name === newFullName);
            if (renamedSection) {
              const updateResult = await updateSection(renamedSection.id, {
                name: newFullName,
                type: renamedSection.type,
                display: renamedSection.display,
                ordre: newOrder,
                importance: renamedSection.importance,
                mandatory_entry: renamedSection.mandatory_entry
              });
              if (!updateResult.success) {
                throw updateResult.error;
              }
            }
          } else {
            // Pas de renommage, juste mettre à jour l'ordre
            const updateResult = await updateSection(draggedSection.id, {
              name: draggedSection.name,
              type: draggedSection.type,
              display: draggedSection.display,
              ordre: newOrder,
              importance: draggedSection.importance,
              mandatory_entry: draggedSection.mandatory_entry
            });
            if (!updateResult.success) {
              throw updateResult.error;
            }
          }
        } else {
          // Même parent, juste changer l'ordre
          const updateResult = await updateSection(draggedSection.id, {
            name: draggedSection.name,
            type: draggedSection.type,
            display: draggedSection.display,
            ordre: newOrder,
            importance: draggedSection.importance,
            mandatory_entry: draggedSection.mandatory_entry
          });
          if (!updateResult.success) {
            throw updateResult.error;
          }
        }
        
        operationSuccess = true;
      }
      
    } catch (error) {
      console.error('❌ Erreur drag & drop:', error);
      operationSuccess = false;
      operationError = error;
    }
    
    // ✅ Réorganiser les sections après le déplacement
    if (operationSuccess) {
      sections.value = organizeByHierarchyAndOrder(sections.value);
      console.log('✅ Déplacement terminé avec succès');
      return { success: true };
    } else {
      return { success: false, error: operationError };
    }
  };

  const autoSetSectionType = async (section: SectionExt): Promise<void> => {
    const hasChildren = sections.value.some(s => s.name.startsWith(section.name + '.'));
    
    if (!hasChildren && (!section.type || section.type.trim() === '')) {
      // Section finale sans type -> définir type par défaut
      await updateSection(section.id, {
        ...section,
        type: 'text'
      });
    } else if (hasChildren && section.type && ['text', 'textarea', 'bool', 'number', 'date'].includes(section.type)) {
      // Section avec enfants qui a un type final -> supprimer le type
      await updateSection(section.id, {
        ...section,
        type: ''
      });
    }
  };

  // Watcher pour mettre à jour automatiquement les listes de problèmes
  watch(sections, () => {
    // Mise à jour silencieuse des listes de problèmes
  }, { deep: true });

  // Fonction de nettoyage global des noms corrompus
  const cleanAllCorruptedNames = async (): Promise<{ success: boolean; cleanedCount: number; error?: unknown }> => {
    try {
      let cleanedCount = 0;
      
      for (const section of sections.value) {
        const originalName = section.name;
        const segments = originalName.split('.');
        const cleanedSegments: string[] = [];
        
        for (let i = 0; i < segments.length; i++) {
          const segment = segments[i];
          if (i === 0 || segment !== segments[i - 1]) {
            cleanedSegments.push(segment);
          }
        }
        
        const cleanedName = cleanedSegments.join('.');
        
        if (originalName !== cleanedName) {
          await sectionsApi.updateSection(section.id, { 
            name: cleanedName,
            type: section.type,
            display: section.display,
            ordre: section.ordre,
            importance: section.importance,
            mandatory_entry: section.mandatory_entry
          });
          cleanedCount++;
          console.log(`✅ Nom corrigé: "${originalName}" → "${cleanedName}"`);
        }
      }
      
      if (cleanedCount > 0) {
        // Recharger les données après le nettoyage
        await fetchSectionsData(true);
        console.log(`🎉 Nettoyage terminé: ${cleanedCount} noms corrompus corrigés`);
      } else {
        console.log('✅ Aucun nom corrompu détecté');
      }
      
      return { success: true, cleanedCount };
    } catch (error) {
      console.error('❌ Erreur lors du nettoyage global:', error);
      return { success: false, cleanedCount: 0, error };
    }
  };

  return {
    // État
    sections: organizedSections,
    translations,
    configs,
    loading,
    error,
    
    // État UI
    collapsedSections,
    editingName,
    editingNameValue,
    
    // État drag & drop
    draggedSection,
    dragOverSection,
    dropPosition,
    
    // État des modaux
    showDeleteModal,
    showTranslationModal,
    showConditionModal,
    showAddRootModal,
    showOrphanModal,
    showGlobalRepairModal,
    
    // Computed
    orphanedSections,
    sectionsWithoutType,
    sectionsWithBrokenConditions,
    problemSections,
    problemCount,
    
    // Actions principales
    fetchSectionsData,
    fetchTranslations,
    fetchConfigs,
    
    // Actions CRUD
    createSection,
    updateSection,
    deleteSection,
    
    // Actions de réparation
    repairSection,
    
    // Actions UI
    toggleCollapse,
    collapseAll,
    expandAll,
    
    // Fonctions utilitaires
    hasChildren,
    isCollapsed,
    shouldShowSection,
    getAllDescendants,
    renameWithCascade,
    
    // Actions drag & drop
    performDrop,
    autoSetSectionType,
    
    // Nettoyage des noms corrompus
    cleanAllCorruptedNames,
  };
}); 